import React, { useState, useEffect } from "react";
import GlassCard from "../components/ui/GlassCard";
import Button from "../components/ui/Button";
import { Input } from "../components/ui/FormFields";
import { Eye, EyeOff } from "lucide-react";
import {
  loginUser,
  createDefaultSuperAdmin,
  createUserAccount,
  createOrganization,
  getUserByAuthUid,
  LoginResult,
  CreateAccountResult
} from "../services/authService";

interface Props {
  onLogin: (user: any) => void;
}

interface TempUser {
  uid: string;
  email: string;
  name: string;
  role: null;
  hasOrganization: boolean;
}

const LoginPage: React.FC<Props> = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showOrgSetup, setShowOrgSetup] = useState(false);
  const [tempUser, setTempUser] = useState<TempUser | null>(null);
  const [tempUserDocId, setTempUserDocId] = useState<string>("");

  // Login/Signup fields
  const [email, setEmail] = useState("");
  const [password, setPass] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");

  // Password visibility states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Organization fields
  const [orgName, setOrgName] = useState("");
  const [orgDomain, setOrgDomain] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#3b82f6");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    createDefaultSuperAdmin();
  }, []);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      setLoading(false);
      return;
    }

    if (!fullName.trim()) {
      setError("Full name is required");
      setLoading(false);
      return;
    }

    const result: CreateAccountResult = await createUserAccount(email, password, fullName);

    if (!result.success) {
      setError(result.error || "Failed to create account");
      setLoading(false);
    } else if (result.user) {
      const userData = await getUserByAuthUid(result.user.uid);
      if (userData && userData.id) {
        setTempUserDocId(userData.id);
      }
      setTempUser(result.user);
      if (result.user.role === "super_admin") {
        onLogin(result.user);
      } else {
        setShowOrgSetup(true);
      }
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const result = await loginUser(email, password);

    if (!result) {
      setError("User not found");
      setLoading(false);
    } else if (result === "wrong_password") {
      setError("Wrong password");
      setLoading(false);
    } else {
      const loginResult = result as LoginResult;
      if (
        loginResult.user &&
        !loginResult.user.hasOrganization &&
        loginResult.user.role !== "super_admin"
      ) {
        const userData = await getUserByAuthUid(loginResult.user.uid);
        if (userData && userData.id) {
          setTempUserDocId(userData.id);
        }
        setTempUser({
          uid: loginResult.user.uid,
          email: loginResult.user.email,
          name: loginResult.user.name,
          role: null,
          hasOrganization: false
        });
        setShowOrgSetup(true);
        setLoading(false);
      } else if (loginResult.user) {
        onLogin(loginResult.user);
        setLoading(false);
      }
    }
  };

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!orgName.trim()) {
      setError("Organization name is required");
      setLoading(false);
      return;
    }

    if (!tempUser) {
      setError("User information missing");
      setLoading(false);
      return;
    }

    const result = await createOrganization(
      tempUser.uid,
      tempUserDocId,
      {
        name: orgName,
        domain: orgDomain,
        plan: "basic",
        primaryColor: primaryColor
      }
    );

    if (result.success) {
      const userWithOrg = {
        ...tempUser,
        organizationId: result.orgId,
        role: "org_admin",
        hasOrganization: true
      };
      onLogin(userWithOrg);
    } else {
      setError(result.error || "Failed to create organization");
      setLoading(false);
    }
  };

  if (showOrgSetup) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          background: "linear-gradient(135deg,#e0e7ff 0%,#f0fdf4 50%,#fef9c3 100%)",
        }}
      >
        <div className="w-full max-w-md space-y-5">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-600 to-indigo-700 flex items-center justify-center text-white font-extrabold text-2xl mx-auto mb-4 shadow-lg">
              PS
            </div>
            <h1 className="text-3xl font-extrabold text-gray-800">Setup Organization</h1>
            <p className="text-gray-600 mt-2">Create your organization to get started</p>
          </div>

          <GlassCard className="p-7">
            <form onSubmit={handleCreateOrganization} className="space-y-4">
              <Input
                label="Organization Name"
                type="text"
                value={orgName}
                onChange={setOrgName}
                placeholder="e.g., TechCorp Solutions"
                required
              />

              <Input
                label="Domain (Optional)"
                type="text"
                value={orgDomain}
                onChange={setOrgDomain}
                placeholder="e.g., techcorp.com"
              />

              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Brand Color
                </label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={primaryColor}
                    onChange={setPrimaryColor}
                    placeholder="#3b82f6"
                    className="flex-1"
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                  <p className="text-red-600 text-xs">{error}</p>
                </div>
              )}

              <Button fullWidth disabled={loading}>
                {loading ? "Creating Organization..." : "Create Organization"}
              </Button>
            </form>
          </GlassCard>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: "linear-gradient(135deg,#e0e7ff 0%,#f0fdf4 50%,#fef9c3 100%)",
      }}
    >
      <div className="w-full max-w-md space-y-5">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-600 to-indigo-700 flex items-center justify-center text-white font-extrabold text-2xl mx-auto mb-4 shadow-lg">
            PS
          </div>
          <h1 className="text-3xl font-extrabold text-gray-800">PsychoAssess</h1>
          <p className="text-gray-600 mt-2">
            {isLogin ? "Welcome back!" : "Create your account"}
          </p>
        </div>

        <GlassCard className="p-7">
          <form onSubmit={isLogin ? handleLogin : handleSignup} className="space-y-4">
            {!isLogin && (
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="John Doe"
                  required
                  className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none"
                />
              </div>
            )}

            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Email *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none"
              />
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Password *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPass(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {!isLogin && (
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Confirm Password *
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all outline-none pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                <p className="text-red-600 text-xs">{error}</p>
              </div>
            )}

            <Button fullWidth disabled={loading}>
              {loading
                ? (isLogin ? "Signing in..." : "Creating Account...")
                : (isLogin ? "Sign In" : "Create Account")}
            </Button>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError("");
                  setEmail("");
                  setPass("");
                  setConfirmPassword("");
                  setFullName("");
                  setShowPassword(false);
                  setShowConfirmPassword(false);
                }}
                className="text-indigo-600 hover:text-indigo-700 text-sm font-medium transition-colors"
              >
                {isLogin
                  ? "Don't have an account? Create one"
                  : "Already have an account? Sign in"}
              </button>
            </div>
          </form>
        </GlassCard>
      </div>
    </div>
  );
};

export default LoginPage;